import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSocketContext } from '../contexts/SocketContext';

interface LobbyState {
  lobbyId: string;
  gameMode: string;
  player1Id: number;
  player2Id: number;
  player1Username?: string;
  player2Username?: string;
  player1Settings: {
    wordLength: number;
    useUmlauts: boolean;
    winCondition?: string; // For race mode: 'fastest' or 'fewest'
  };
  player2Settings: {
    wordLength: number;
    useUmlauts: boolean;
    winCondition?: string; // For race mode: 'fastest' or 'fewest'
  };
  player1Ready: boolean;
  player2Ready: boolean;
}

const GameLobby: React.FC = () => {
  const [searchParams] = useSearchParams();
  const lobbyId = searchParams.get('lobbyId');
  const navigate = useNavigate();
  const { socket } = useSocketContext();
  const currentUserIdRef = React.useRef<number | null>(null);

  const [lobbyState, setLobbyState] = useState<LobbyState | null>(null);
  const [mySettings, setMySettings] = useState<{ wordLength: number; useUmlauts: boolean; winCondition?: string } | null>(null);
  const [myReady, setMyReady] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  
  // Determine which player we are based on lobby state
  // This will be set when lobby-state is received
  const isPlayer1Ref = React.useRef<boolean | null>(null);

  useEffect(() => {
    if (!socket || !lobbyId) {
      setError('Lobby-ID fehlt');
      setLoading(false);
      return;
    }

    // Register event listeners first, before joining lobby
    const handleLobbyState = (data: LobbyState) => {
      setLobbyState(data);
      
      // Determine which player I am and set my settings
      // Player 1 is the one who sent the invitation (left side)
      // Player 2 is the one who accepted (right side)
      if (data.player1Id === currentUserIdRef.current) {
        isPlayer1Ref.current = true;
        setMySettings(data.player1Settings);
        setMyReady(data.player1Ready);
      } else if (data.player2Id === currentUserIdRef.current) {
        isPlayer1Ref.current = false;
        setMySettings(data.player2Settings);
        setMyReady(data.player2Ready);
      }
      
      setLoading(false);
    };

    socket.on('lobby-state', handleLobbyState);

    // Get current user ID and then join lobby
    fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/auth/me`, { 
      credentials: 'include' 
    })
      .then(res => res.json())
      .then(data => {
        if (data.authenticated) {
          currentUserIdRef.current = data.user.id;
        }
        // Join lobby after user ID is set
        socket.emit('join-lobby', { lobbyId });
      })
      .catch(err => {
        console.error('Error fetching user:', err);
        // Still try to join lobby even if user fetch fails
        socket.emit('join-lobby', { lobbyId });
      });

    socket.on('lobby-settings-changed', (data: LobbyState) => {
      setLobbyState(data);
      
      // Update my settings if I'm the one who changed them
      if (data.player1Id === currentUserIdRef.current) {
        isPlayer1Ref.current = true;
        setMySettings(data.player1Settings);
        setMyReady(data.player1Ready);
      } else if (data.player2Id === currentUserIdRef.current) {
        isPlayer1Ref.current = false;
        setMySettings(data.player2Settings);
        setMyReady(data.player2Ready);
      }
    });

    socket.on('lobby-settings-mismatch', (data: { message: string }) => {
      setError(data.message);
      setMyReady(false);
      setTimeout(() => setError(''), 5000);
    });

    socket.on('lobby-error', (data: { message: string }) => {
      setError(data.message);
      setTimeout(() => {
        navigate('/menu');
      }, 3000);
    });

    socket.on('game-started', (data: any) => {
      navigate(`/game?type=multiplayer&mode=${data.gameMode}&gameId=${data.gameId}`);
    });

    socket.on('lobby-closed', (data: { message: string }) => {
      alert(data.message || 'Die Lobby wurde vom anderen Spieler verlassen.');
      navigate('/menu');
    });

    return () => {
      socket.off('lobby-state', handleLobbyState);
      socket.off('lobby-settings-changed');
      socket.off('lobby-settings-mismatch');
      socket.off('lobby-error');
      socket.off('game-started');
      socket.off('lobby-closed');
    };
  }, [socket, lobbyId, navigate]);

  const handleSettingsChange = (key: 'wordLength' | 'useUmlauts' | 'winCondition', value: number | boolean | string) => {
    if (!socket || !lobbyId || !mySettings) return;
    
    const newSettings = { ...mySettings, [key]: value };
    setMySettings(newSettings);
    setMyReady(false); // Reset ready when settings change
    
    socket.emit('lobby-settings-update', {
      lobbyId,
      wordLength: newSettings.wordLength,
      useUmlauts: newSettings.useUmlauts,
      winCondition: newSettings.winCondition
    });
  };

  const handleReady = () => {
    if (!socket || !lobbyId) return;
    
    setMyReady(true);
    socket.emit('lobby-ready', { lobbyId });
  };

  const handleNotReady = () => {
    if (!socket || !lobbyId || !mySettings) return;
    
    setMyReady(false);
    // Reset ready status by updating settings (which resets ready)
    socket.emit('lobby-settings-update', {
      lobbyId,
      wordLength: mySettings.wordLength,
      useUmlauts: mySettings.useUmlauts,
      winCondition: mySettings.winCondition
    });
  };

  const handleLeaveLobby = () => {
    setShowLeaveConfirm(true);
  };

  const confirmLeaveLobby = () => {
    if (!socket || !lobbyId) return;
    
    socket.emit('leave-lobby', { lobbyId });
    navigate('/menu');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
        <div className="text-white text-xl">Lobby wird geladen...</div>
      </div>
    );
  }

  if (!lobbyState) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 text-center">
          <p className="text-gray-800 mb-4">Lobby nicht gefunden</p>
          <button
            onClick={() => navigate('/menu')}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700"
          >
            Zurück zum Menü
          </button>
        </div>
      </div>
    );
  }

  // Determine which player we are
  // Player 1 is the one who sent the invitation (left side)
  // Player 2 is the one who accepted (right side)
  const isPlayer1 = isPlayer1Ref.current !== null 
    ? isPlayer1Ref.current 
    : lobbyState.player1Id === currentUserIdRef.current;
  const opponentSettings = isPlayer1 ? lobbyState.player2Settings : lobbyState.player1Settings;
  const opponentReady = isPlayer1 ? lobbyState.player2Ready : lobbyState.player1Ready;
  let settingsMatch = mySettings !== null &&
    mySettings.wordLength === opponentSettings.wordLength &&
    mySettings.useUmlauts === opponentSettings.useUmlauts;
  
  // For race mode, also check winCondition
  if (lobbyState.gameMode === 'race') {
    const myWinCondition = mySettings?.winCondition || 'fastest';
    const opponentWinCondition = opponentSettings.winCondition || 'fastest';
    settingsMatch = settingsMatch && myWinCondition === opponentWinCondition;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-500 to-purple-600 p-4">
      <div className="max-w-6xl mx-auto pt-8">
        <div className="bg-white rounded-2xl shadow-2xl p-8">
          <h1 className="text-3xl font-bold text-center mb-6 text-gray-800">
            {lobbyState.gameMode === 'alternating' ? 'Gemeinsames Frördle' : '1 vs 1 Frördle'}
          </h1>

          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg mb-6">
              {error}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            {/* Player 1 (Left) */}
            <div className={`border-2 rounded-lg p-6 ${isPlayer1 ? 'border-blue-500 bg-blue-50' : 'border-gray-300'}`}>
              <h2 className="text-xl font-bold mb-4 text-gray-800">
                {isPlayer1 ? 'Deine Einstellungen' : 'Spieler 1'}
              </h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Wortlänge
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {[3, 4, 5, 6].map((length) => (
                      <button
                        key={length}
                        onClick={() => isPlayer1 && handleSettingsChange('wordLength', length)}
                        disabled={!isPlayer1}
                        className={`py-2 px-4 rounded-lg font-semibold transition-colors ${
                          (isPlayer1 ? (mySettings?.wordLength || 5) : lobbyState.player1Settings.wordLength) === length
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
                        } ${!isPlayer1 ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        {length}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Umlaute
                  </label>
                  <div className="flex gap-2">
                    <button
                      onClick={() => isPlayer1 && handleSettingsChange('useUmlauts', true)}
                      disabled={!isPlayer1}
                      className={`flex-1 py-2 px-4 rounded-lg font-semibold transition-colors ${
                        (isPlayer1 ? (mySettings?.useUmlauts ?? false) : lobbyState.player1Settings.useUmlauts)
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
                      } ${!isPlayer1 ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      Mit Umlauten
                    </button>
                    <button
                      onClick={() => isPlayer1 && handleSettingsChange('useUmlauts', false)}
                      disabled={!isPlayer1}
                      className={`flex-1 py-2 px-4 rounded-lg font-semibold transition-colors ${
                        !(isPlayer1 ? (mySettings?.useUmlauts ?? false) : lobbyState.player1Settings.useUmlauts)
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
                      } ${!isPlayer1 ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      Ohne Umlaute
                    </button>
                  </div>
                </div>

                {/* Win Condition (only for race mode) */}
                {lobbyState.gameMode === 'race' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Siegbedingung
                    </label>
                    <div className="flex gap-2">
                      <button
                        onClick={() => isPlayer1 && handleSettingsChange('winCondition', 'fastest')}
                        disabled={!isPlayer1}
                        className={`flex-1 py-2 px-4 rounded-lg font-semibold transition-colors ${
                          (isPlayer1 ? (mySettings?.winCondition || 'fastest') : (lobbyState.player1Settings.winCondition || 'fastest')) === 'fastest'
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
                        } ${!isPlayer1 ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        Schnellstes Erraten
                      </button>
                      <button
                        onClick={() => isPlayer1 && handleSettingsChange('winCondition', 'fewest')}
                        disabled={!isPlayer1}
                        className={`flex-1 py-2 px-4 rounded-lg font-semibold transition-colors ${
                          (isPlayer1 ? (mySettings?.winCondition || 'fastest') : (lobbyState.player1Settings.winCondition || 'fastest')) === 'fewest'
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
                        } ${!isPlayer1 ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        Wenigste Versuche
                      </button>
                    </div>
                  </div>
                )}

                {isPlayer1 && (
                  <div className="pt-4">
                    {!myReady ? (
                      <button
                        onClick={handleReady}
                        className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-6 rounded-lg transition duration-200"
                      >
                        Annehmen
                      </button>
                    ) : (
                      <button
                        onClick={handleNotReady}
                        className="w-full bg-yellow-600 hover:bg-yellow-700 text-white font-semibold py-3 px-6 rounded-lg transition duration-200"
                      >
                        Ändern
                      </button>
                    )}
                  </div>
                )}

                {!isPlayer1 && (
                  <div className="pt-4">
                    <div className={`text-center py-3 px-6 rounded-lg font-semibold ${
                      lobbyState.player1Ready ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                    }`}>
                      {lobbyState.player1Ready ? '✓ Bereit' : 'Wartet...'}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Player 2 (Right) */}
            <div className={`border-2 rounded-lg p-6 ${!isPlayer1 ? 'border-blue-500 bg-blue-50' : 'border-gray-300'}`}>
              <h2 className="text-xl font-bold mb-4 text-gray-800">
                {!isPlayer1 ? 'Deine Einstellungen' : (lobbyState.player2Username || 'Spieler 2')}
              </h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Wortlänge
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {[3, 4, 5, 6].map((length) => (
                      <button
                        key={length}
                        onClick={() => !isPlayer1 && handleSettingsChange('wordLength', length)}
                        disabled={isPlayer1}
                        className={`py-2 px-4 rounded-lg font-semibold transition-colors ${
                          (!isPlayer1 ? (mySettings?.wordLength || 5) : lobbyState.player2Settings.wordLength) === length
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
                        } ${isPlayer1 ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        {length}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Umlaute
                  </label>
                  <div className="flex gap-2">
                    <button
                      onClick={() => !isPlayer1 && handleSettingsChange('useUmlauts', true)}
                      disabled={isPlayer1}
                      className={`flex-1 py-2 px-4 rounded-lg font-semibold transition-colors ${
                        (!isPlayer1 ? (mySettings?.useUmlauts ?? false) : lobbyState.player2Settings.useUmlauts)
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
                      } ${isPlayer1 ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      Mit Umlauten
                    </button>
                    <button
                      onClick={() => !isPlayer1 && handleSettingsChange('useUmlauts', false)}
                      disabled={isPlayer1}
                      className={`flex-1 py-2 px-4 rounded-lg font-semibold transition-colors ${
                        !(!isPlayer1 ? (mySettings?.useUmlauts ?? true) : lobbyState.player2Settings.useUmlauts)
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
                      } ${isPlayer1 ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      Ohne Umlaute
                    </button>
                  </div>
                </div>

                {/* Win Condition (only for race mode) */}
                {lobbyState.gameMode === 'race' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Siegbedingung
                    </label>
                    <div className="flex gap-2">
                      <button
                        onClick={() => !isPlayer1 && handleSettingsChange('winCondition', 'fastest')}
                        disabled={isPlayer1}
                        className={`flex-1 py-2 px-4 rounded-lg font-semibold transition-colors ${
                          (!isPlayer1 ? (mySettings?.winCondition || 'fastest') : (lobbyState.player2Settings.winCondition || 'fastest')) === 'fastest'
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
                        } ${isPlayer1 ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        Schnellstes Erraten
                      </button>
                      <button
                        onClick={() => !isPlayer1 && handleSettingsChange('winCondition', 'fewest')}
                        disabled={isPlayer1}
                        className={`flex-1 py-2 px-4 rounded-lg font-semibold transition-colors ${
                          (!isPlayer1 ? (mySettings?.winCondition || 'fastest') : (lobbyState.player2Settings.winCondition || 'fastest')) === 'fewest'
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
                        } ${isPlayer1 ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        Wenigste Versuche
                      </button>
                    </div>
                  </div>
                )}

                {!isPlayer1 && (
                  <div className="pt-4">
                    {!myReady ? (
                      <button
                        onClick={handleReady}
                        className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-6 rounded-lg transition duration-200"
                      >
                        Annehmen
                      </button>
                    ) : (
                      <button
                        onClick={handleNotReady}
                        className="w-full bg-yellow-600 hover:bg-yellow-700 text-white font-semibold py-3 px-6 rounded-lg transition duration-200"
                      >
                        Ändern
                      </button>
                    )}
                  </div>
                )}

                {isPlayer1 && (
                  <div className="pt-4">
                    <div className={`text-center py-3 px-6 rounded-lg font-semibold ${
                      lobbyState.player2Ready ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                    }`}>
                      {lobbyState.player2Ready ? '✓ Bereit' : 'Wartet...'}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Status Message */}
          <div className="text-center mb-6">
            {lobbyState.player1Ready && lobbyState.player2Ready && settingsMatch ? (
              <p className="text-green-600 font-semibold text-lg">
                Beide Spieler sind bereit! Spiel startet...
              </p>
            ) : lobbyState.player1Ready && lobbyState.player2Ready && !settingsMatch ? (
              <p className="text-red-600 font-semibold text-lg">
                Einstellungen stimmen nicht überein. Bitte anpassen.
              </p>
            ) : (
              <p className="text-gray-600">
                Warten auf beide Spieler...
              </p>
            )}
          </div>

          <button
            onClick={handleLeaveLobby}
            className="w-full bg-gray-600 hover:bg-gray-700 text-white font-semibold py-3 px-6 rounded-lg transition duration-200"
          >
            Lobby verlassen
          </button>
          
          {/* Leave confirmation modal */}
          {showLeaveConfirm && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-md w-full">
                <h2 className="text-2xl font-bold text-gray-800 mb-4">
                  Lobby verlassen?
                </h2>
                <p className="text-gray-600 mb-6">
                  Möchtest du die Lobby wirklich verlassen? Die Lobby wird dann aufgelöst und der andere Spieler wird ins Hauptmenü zurückgeführt.
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={confirmLeaveLobby}
                    className="flex-1 bg-red-600 hover:bg-red-700 text-white font-semibold py-3 px-6 rounded-lg transition duration-200 min-h-[44px]"
                  >
                    Ja, verlassen
                  </button>
                  <button
                    onClick={() => setShowLeaveConfirm(false)}
                    className="flex-1 bg-gray-600 hover:bg-gray-700 text-white font-semibold py-3 px-6 rounded-lg transition duration-200 min-h-[44px]"
                  >
                    Abbrechen
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GameLobby;

